import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightSmallIcon from '@mui/icons-material/KeyboardArrowRight';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Karta } from '@/components/Karta';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { Segmenty, type Segment } from '@/components/Segmenty';
import { useRozliczenia } from '@/lib/useAnalytics';
import { RozliczeniaMiesieczne } from './RozliczeniaMiesieczne';
import { RozliczeniaRoczne } from './RozliczeniaRoczne';
import { formatCount, formatDokladnie, formatGrosze, formatStawka, formatZlote, odmiana } from '@/lib/format';

const ROZLICZENIE = ['rozliczenie', 'rozliczenia', 'rozliczeń'] as const;
const POZYCJA = ['pozycja', 'pozycje', 'pozycji'] as const;
const RESTAURACJA = ['restauracja', 'restauracje', 'restauracji'] as const;
import { liczbaTygodni, okresTygodnia, przesun, type TydzienIso } from '@/lib/rozliczenia/tydzien';
import type {
  KwotyDokladne, PowodBrakuKwot, PowodNieswiezosci, Rozliczenie, RozliczeniaOdpowiedz, SwiezoscRozliczen,
  WidocznoscRozliczen,
} from '@/lib/api/analytics';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * RESTAURACJE → ROZLICZENIA TYGODNIOWE. Ile należy się restauracji za tydzień.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * TYLKO ODCZYT. Ekran nie oznacza wypłat, nie zamyka tygodni i nie zmienia stawek — nie ma tu
 * ani jednego przycisku zapisu. Każda liczba pochodzi z serwera, który liczy rozliczenie od nowa
 * przy każdym odczycie, na PEŁNYM zbiorze transakcji (nie z paginowanej tabeli Transakcji).
 *
 *   obrót          dokładnie co do grosza, dzień = dzień DOSTAWY (pon–pt)
 *   marża netto    Warszawa 19%, pozostałe miasta 17% (z ID restauracji, nie z nazwy)
 *   VAT od marży   23%
 *   do wypłaty     obrót − prowizja brutto, zaokrąglone RAZ do pełnych złotych
 *
 * Kwoty w pełnych złotych pokazujemy bez „,00”, a obrót z groszami — żeby po samym zapisie było
 * widać, która liczba jest dokładna, a która zaokrąglona. Dokładna wartość jest w podpowiedzi.
 */

const DNI = ['PON', 'WT', 'ŚR', 'CZW', 'PT'] as const;
const PIERWSZY_ROK = 2025;

const POWODY: Record<PowodBrakuKwot, { etykieta: string; opis: string }> = {
  AMOUNT_NOT_PROVABLE: {
    etykieta: 'kwota niepotwierdzona',
    opis: 'W tym tygodniu są pozycje bez udowodnionej kwoty pobranej (np. rabat szkoły, a replika nie ma jeszcze kolumny kwoty pobranej). Obrót jest pokazany informacyjnie; prowizja i wypłata są wstrzymane, zamiast liczyć je z ceny katalogowej.',
  },
  MARGIN_UNKNOWN: {
    etykieta: 'brak stawki',
    opis: 'Restauracji nie ma w konfiguracji rozliczeń (miasto → marża). Kwoty nie są liczone, dopóki nie zostanie dopisana — stawka nie jest zgadywana.',
  },
};

const liczby = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

function dzisWarszawa(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Warsaw' });
}

function dzienMiesiac(data: string): string {
  const [, m, d] = data.split('-');
  return `${d}.${m}`;
}

function pelnaData(data: string): string {
  const [r, m, d] = data.split('-');
  return `${d}.${m}.${r}`;
}

function czasSynchronizacji(iso: string | null): string {
  if (!iso) return 'brak danych';
  return new Date(iso).toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Tydzień z adresu (?rok=2026&tydzien=40) — da się go wkleić koledze i odtworzyć ten sam widok. */
function tydzienZAdresu(p: URLSearchParams): TydzienIso | undefined {
  const rok = Number(p.get('rok'));
  const tydzien = Number(p.get('tydzien'));
  if (!Number.isInteger(rok) || !Number.isInteger(tydzien) || rok < 2016 || rok > 2100) return undefined;
  if (tydzien < 1 || tydzien > liczbaTygodni(rok)) return undefined;
  return { rokIso: rok, tydzien };
}

function stawkaRozliczenia(s: Rozliczenie): string {
  if (s.commissionNetRate !== null) return formatStawka(s.commissionNetRate);
  if (s.marginsNet.length > 1) return s.marginsNet.map(formatStawka).join(' / ');
  return '—';
}

/** Kwota zaokrąglona z dokładną wartością w podpowiedzi. */
function Kwota({ grosze, dokladnie, wyrozniona = false }: { grosze: number | null; dokladnie?: string; wyrozniona?: boolean }) {
  const tekst = formatZlote(grosze);
  const tresc = (
    <Box component="span" sx={{ ...liczby, fontWeight: wyrozniona ? 700 : undefined }}>{tekst}</Box>
  );
  if (!dokladnie || grosze === null) return tresc;
  return <Tooltip title={`dokładnie ${formatDokladnie(dokladnie)} — zaokrąglone raz do pełnych złotych`}>{tresc}</Tooltip>;
}

function PowodChip({ powod }: { powod: PowodBrakuKwot }) {
  const p = POWODY[powod];
  return (
    <Tooltip title={p.opis}>
      <Chip size="small" color="warning" variant="outlined" label={p.etykieta} />
    </Tooltip>
  );
}

/** Komórki kwot rozliczenia: marża, VAT, brutto, do wypłaty — albo powód, dla którego ich nie ma. */
function KomorkiKwot({ s, wyciszone }: { s: Rozliczenie; wyciszone: boolean }) {
  if (s.amountsUnavailableReason) {
    return (
      <>
        <TableCell align="right" colSpan={3}>
          <PowodChip powod={s.amountsUnavailableReason} />
        </TableCell>
        <TableCell align="right" sx={{ bgcolor: 'action.hover' }}>—</TableCell>
      </>
    );
  }
  const e: KwotyDokladne | null = s.exact;
  const kolor = wyciszone ? 'text.disabled' : undefined;
  return (
    <>
      <TableCell align="right" sx={{ color: kolor }}>
        <Kwota grosze={s.commissionNetGrosze} dokladnie={e?.prowizjaNetto} />
        <Typography component="div" variant="caption" sx={{ color: 'text.secondary' }}>{stawkaRozliczenia(s)}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ color: kolor }}>
        <Kwota grosze={s.commissionVatGrosze} dokladnie={e?.vat} />
      </TableCell>
      <TableCell align="right" sx={{ color: kolor }}>
        <Kwota grosze={s.commissionGrossGrosze} dokladnie={e?.prowizjaBrutto} />
      </TableCell>
      <TableCell align="right" sx={{ bgcolor: 'action.hover', color: kolor, fontSize: '0.95rem' }}>
        <Kwota grosze={s.payoutGrosze} dokladnie={e?.doWyplaty} wyrozniona={!wyciszone} />
      </TableCell>
    </>
  );
}

const pierwszaKolumna = {
  position: 'sticky',
  left: 0,
  bgcolor: 'background.paper',
  zIndex: 1,
  minWidth: { xs: 150, sm: 220 },
} as const;

function WierszRozliczenia({ s, rozwiniety, przelacz }: { s: Rozliczenie; rozwiniety: boolean; przelacz: () => void }) {
  const wyciszone = s.turnoverGrosze === 0;
  const kolor = wyciszone ? 'text.disabled' : undefined;
  const zablokowana = !s.isGroup && s.members[0]?.blocked;
  return (
    <>
      <TableRow hover data-rozliczenie={s.settlementGroupId}>
        <TableCell sx={{ ...pierwszaKolumna, color: kolor }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            {s.isGroup ? (
              <IconButton
                size="small"
                onClick={przelacz}
                aria-expanded={rozwiniety}
                aria-label={`${rozwiniety ? 'Zwiń' : 'Rozwiń'} grupę ${s.settlementGroupName}`}
              >
                {rozwiniety ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightSmallIcon fontSize="small" />}
              </IconButton>
            ) : <Box sx={{ width: 30 }} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: s.isGroup ? 600 : undefined, color: kolor }}>
                {s.settlementGroupName}
              </Typography>
              {s.isGroup && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  grupa · {new Set(s.members.map((m) => m.restaurantId)).size} {odmiana(new Set(s.members.map((m) => m.restaurantId)).size, RESTAURACJA)}
                </Typography>
              )}
              {zablokowana && <Chip size="small" variant="outlined" label="zablokowana" sx={{ ml: 0.5 }} />}
            </Box>
          </Stack>
        </TableCell>
        <TableCell sx={{ color: kolor, whiteSpace: 'nowrap' }}>{s.cities.join(', ') || '—'}</TableCell>
        {s.turnoverByDayGrosze.map((g, i) => (
          <TableCell key={DNI[i]} align="right" sx={{ ...liczby, color: g === 0 ? 'text.disabled' : kolor }}>
            {formatGrosze(g)}
          </TableCell>
        ))}
        <TableCell align="right" sx={{ ...liczby, fontWeight: 600, color: kolor }}>
          <Tooltip title={`${formatCount(s.positions)} ${odmiana(s.positions, POZYCJA)} zamówień`}>
            <span>{formatGrosze(s.turnoverGrosze)}</span>
          </Tooltip>
        </TableCell>
        <KomorkiKwot s={s} wyciszone={wyciszone} />
      </TableRow>

      {s.isGroup && rozwiniety && s.members.map((m) => (
        <TableRow key={m.memberKey ?? m.restaurantId} data-czlonek={m.memberKey ?? m.restaurantId}>
          <TableCell sx={{ ...pierwszaKolumna, pl: 6 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{m.name}</Typography>
            {m.blocked && <Chip size="small" variant="outlined" label="zablokowana" />}
          </TableCell>
          <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{m.city ?? '—'}</TableCell>
          {m.turnoverByDayGrosze.map((g, i) => (
            <TableCell key={DNI[i]} align="right" sx={{ ...liczby, color: 'text.secondary' }}>{formatGrosze(g)}</TableCell>
          ))}
          <TableCell align="right" sx={{ ...liczby, color: 'text.secondary' }}>{formatGrosze(m.turnoverGrosze)}</TableCell>
          <TableCell align="right" sx={{ color: 'text.secondary' }}>
            <Typography variant="caption">{formatStawka(m.marginNet)}</Typography>
          </TableCell>
          <TableCell colSpan={3} align="right">
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>w rozliczeniu grupy</Typography>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function WyborTygodnia({ okres, ustaw, domyslny }: {
  okres: TydzienIso;
  ustaw: (t: TydzienIso | undefined) => void;
  domyslny: boolean;
}) {
  const biezacyRok = Number(dzisWarszawa().slice(0, 4));
  const lata = [];
  for (let r = PIERWSZY_ROK; r <= biezacyRok + 1; r += 1) lata.push(r);
  const tygodnie = Array.from({ length: liczbaTygodni(okres.rokIso) }, (_, i) => i + 1);
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <IconButton aria-label="Poprzedni tydzień" onClick={() => ustaw(przesun(okres, -1))}>
        <ChevronLeftIcon />
      </IconButton>
      <TextField
        select size="small" label="Rok" value={okres.rokIso} sx={{ width: 96 }}
        onChange={(e) => {
          const rok = Number(e.target.value);
          ustaw({ rokIso: rok, tydzien: Math.min(okres.tydzien, liczbaTygodni(rok)) });
        }}
      >
        {lata.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
      </TextField>
      <TextField
        select size="small" label="Tydzień" value={okres.tydzien} sx={{ width: 110 }}
        onChange={(e) => ustaw({ rokIso: okres.rokIso, tydzien: Number(e.target.value) })}
      >
        {tygodnie.map((t) => <MenuItem key={t} value={t}>{String(t).padStart(2, '0')}</MenuItem>)}
      </TextField>
      <IconButton aria-label="Następny tydzień" onClick={() => ustaw(przesun(okres, 1))}>
        <ChevronRightIcon />
      </IconButton>
      <Button size="small" variant="text" disabled={domyslny} onClick={() => ustaw(undefined)}>
        Ostatni zakończony
      </Button>
    </Stack>
  );
}

const POWODY_NIESWIEZOSCI: Record<PowodNieswiezosci, string> = {
  FULL_SYNC_STALE: 'Pełna synchronizacja jest starsza niż 26 h — usunięcia i korekty ze starszych zamówień mogą nie być uwzględnione.',
  INCREMENTAL_SYNC_STALE: 'Synchronizacja przyrostowa nie odświeżyła się od ponad 30 minut w godzinach, w których powinna działać.',
  SYNC_FAILED: 'Ostatni przebieg synchronizacji zakończył się błędem.',
  FRESHNESS_UNKNOWN: 'Nie da się odczytać przebiegów synchronizacji — świeżości danych nie można potwierdzić.',
  OLDER_ORDER_CORRECTIONS_NOT_LIVE: 'Korekty i usunięcia zamówień złożonych ponad 2 dni temu (także dla tego okresu) trafiają tu dopiero po pełnej synchronizacji — kwoty mogą ich jeszcze nie uwzględniać.',
  VIEW_OUTDATED: 'Dane w replice zmieniły się, a przeliczenie widoku się nie powiodło — pokazane kwoty są starsze niż dane. Ponowna próba za chwilę.',
};

/** „12 s”, „4 min”, „3 h 5 min” — rzeczywista zaległość od ostatniej udanej aktualizacji. */
function opisZaleglosci(s: number | null | undefined): string | null {
  if (s === null || s === undefined) return null;
  if (s < 60) return `${s} s`;
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

const NIEPEWNE = 'Stan repliki jest nieaktualny albo niepotwierdzony — kwoty mogą nie uwzględniać ostatnich usunięć i korekt.';

/**
 * Z kiedy jest stan, na którym liczona jest wypłata. Zawsze widoczne — przy nieświeżym stanie jako
 * ostrzeżenie z nazwanym powodem. Ostrzeżenie NIE zastępuje szybkiej propagacji usunięć (R5-04).
 */
function PanelSwiezosci({ f }: { f: SwiezoscRozliczen }) {
  const nieswieze = f.state !== 'FRESH';
  const zaleglosc = opisZaleglosci(f.lagSeconds);
  return (
    <Alert severity={nieswieze ? 'warning' : 'info'} data-swiezosc={f.state}>
      {f.state === 'STALE' && <strong>Dane rozliczeń mogą być nieaktualne. </strong>}
      {f.state === 'PARTIAL' && <strong>Zgodności ze źródłem nie da się w pełni potwierdzić. </strong>}
      {f.lastSuccessfulUpdate !== undefined && (
        <Box component="span" sx={{ display: 'block' }} data-ostatnia-aktualizacja>
          Ostatnia udana aktualizacja danych: <strong>{czasSynchronizacji(f.lastSuccessfulUpdate ?? null)}</strong>
          {zaleglosc && <> (zaległość: <strong>{zaleglosc}</strong>)</>}.
        </Box>
      )}
      {f.historyConfirmedAt !== undefined && (
        <Box component="span" sx={{ display: 'block' }} data-historia-potwierdzona>
          Korekty i usunięcia starszych zamówień uwzględnione na stan: <strong>{czasSynchronizacji(f.historyConfirmedAt ?? null)}</strong>
          {f.historyChangesLive ? ' (na bieżąco).' : ' (pełna synchronizacja co noc 02:00 UTC).'}
        </Box>
      )}
      <Box component="span" sx={{ display: 'block' }}>
        Ostatnia pełna synchronizacja: <strong>{czasSynchronizacji(f.lastFullSync)}</strong>
        {' '}(co noc 02:00 UTC).
      </Box>
      <Box component="span" sx={{ display: 'block' }}>
        Ostatnia synchronizacja bieżąca: <strong>{czasSynchronizacji(f.lastIncrementalSync)}</strong>
        {' '}(nowe zamówienia i zmiany zamówień z ostatnich 2 dni, co ok. 20 s).
      </Box>
      {nieswieze && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {f.warnings.map((w) => <li key={w}>{POWODY_NIESWIEZOSCI[w] ?? w}</li>)}
        </Box>
      )}
    </Alert>
  );
}

/**
 * Filtr aktywnych restauracji (OWNER 2026-10-03). Liczy SERWER: lista zawiera WYŁĄCZNIE rozliczenia z obrotem
 * od 1.09.2026 do dziś (kwalifikacja niezależna od wybranego tygodnia; grupa — suma obrotu członków).
 * „Razem” sumuje widoczne rozliczenia.
 */
function PanelWidocznosci({ w }: { w: WidocznoscRozliczen }) {
  return (
    <Alert severity="info" data-widocznosc={`${w.visibleSettlements}/${w.hiddenSettlements}`}>
      <Box component="span" sx={{ display: 'block' }}>
        Widoczne rozliczenia: <strong>{formatCount(w.visibleSettlements)}</strong> · ukryte: <strong>{formatCount(w.hiddenSettlements)}</strong>
        {' '}— pokazujemy wyłącznie restauracje i grupy z obrotem od {pelnaData(w.qualificationFrom)} do dziś ({pelnaData(w.qualificationTo)}).
        Grupę kwalifikuje suma obrotu jej restauracji, wybrany tydzień nie zmienia listy, a „Razem” sumuje widoczne rozliczenia.
      </Box>
      {w.hidden.length > 0 && (
        <Box component="details" sx={{ mt: 0.5 }}>
          <Box component="summary" sx={{ cursor: 'pointer' }}>Pokaż ukryte ({formatCount(w.hidden.length)})</Box>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {w.hidden.map((h) => (
              <li key={h.settlementGroupId}>
                {h.settlementGroupName}
                {(h.positionsWithoutAmountSince ?? 0) > 0 && ` — pozycje bez udowodnionej kwoty od ${pelnaData(w.qualificationFrom)}: ${formatCount(h.positionsWithoutAmountSince ?? 0)}`}
              </li>
            ))}
          </Box>
        </Box>
      )}
    </Alert>
  );
}

/**
 * Suma w pełnych złotych z count-upem (OWNER 2026-10-04: animują się OBRÓT TYGODNIA, PROWIZJA,
 * DO WYPŁATY i sumy — wyłącznie wiersz „Razem”, nigdy wiersze rozliczeń).
 * Krok 100 groszy: każda klatka jest pełną złotówką, więc `formatZlote` nie przeskakuje między
 * „1234 zł” i „1234,56 zł”. Kwota z groszami (gdyby trafiła tu przez pomyłkę) liczy się co grosz.
 */
function KwotaZlotychAnimowana({ grosze }: { grosze: number | null }) {
  return (
    <AnimatedNumber
      value={grosze}
      format={formatZlote}
      step={grosze !== null && grosze % 100 === 0 ? 100 : 1}
    />
  );
}

function WierszSumy({ dane }: { dane: RozliczeniaOdpowiedz }) {
  const t = dane.totals;
  const niepewne = dane.freshness.state !== 'FRESH';
  const komorka = { ...liczby, fontWeight: 700, position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 1, color: 'text.primary', fontSize: '0.875rem' } as const;
  return (
    <TableFooter>
      <TableRow>
        <TableCell sx={{ ...komorka, left: 0, zIndex: 3 }}>Razem ({formatCount(dane.settlements.length)} {odmiana(dane.settlements.length, ROZLICZENIE)})</TableCell>
        <TableCell sx={komorka} />
        {t.turnoverByDayGrosze.map((g, i) => (
          <TableCell key={DNI[i]} align="right" sx={komorka}><AnimatedNumber value={g} format={formatGrosze} /></TableCell>
        ))}
        <TableCell align="right" sx={komorka}><AnimatedNumber value={t.turnoverGrosze} format={formatGrosze} /></TableCell>
        <TableCell align="right" sx={komorka}><KwotaZlotychAnimowana grosze={t.commissionNetGrosze} /></TableCell>
        <TableCell align="right" sx={komorka}><KwotaZlotychAnimowana grosze={t.commissionVatGrosze} /></TableCell>
        <TableCell align="right" sx={komorka}><KwotaZlotychAnimowana grosze={t.commissionGrossGrosze} /></TableCell>
        <TableCell align="right" sx={{ ...komorka, bgcolor: 'action.selected', fontSize: '1rem' }}>
          {t.payoutGrosze === null
            ? <Tooltip title="Suma wypłat nie jest pokazywana, gdy choć jedno rozliczenie nie ma kwot — byłaby niepełna."><span>—</span></Tooltip>
            : <KwotaZlotychAnimowana grosze={t.payoutGrosze} />}
          {niepewne && t.payoutGrosze !== null && (
            <Tooltip title={NIEPEWNE}>
              <Chip size="small" color="warning" variant="outlined" label="niepewne" sx={{ ml: 0.5 }} data-niepewne="suma" />
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    </TableFooter>
  );
}

type WidokRozliczen = 'tygodniowe' | 'miesieczne' | 'roczne';
const WIDOKI: readonly Segment<WidokRozliczen>[] = [
  { wartosc: 'tygodniowe', etykieta: 'Tygodniowe' },
  { wartosc: 'miesieczne', etykieta: 'Miesięczne' },
  { wartosc: 'roczne', etykieta: 'Roczne' },
];

/**
 * RESTAURACJE → ROZLICZENIA: przełącznik widoku (OWNER 2026-10-07). Tygodniowe — bez zmian;
 * Miesięczne — suma tygodni do faktur TuSzamy; Roczne — arkusz „Wykresy Obrotów i Zysków”
 * (rok rozliczeniowy IX..VIII z miesięcy). Widok jest w adresie
 * (?widok=miesieczne), a przełączenie czyści parametry poprzedniego widoku (rok ISO ≠ rok kalendarza).
 */
export function RozliczeniaPage() {
  const [parametry, ustawParametry] = useSearchParams();
  const surowy = parametry.get('widok');
  const widok: WidokRozliczen = surowy === 'miesieczne' || surowy === 'roczne' ? surowy : 'tygodniowe';
  const ustawWidok = (w: WidokRozliczen) => {
    const p = new URLSearchParams();
    if (w !== 'tygodniowe') p.set('widok', w);
    ustawParametry(p, { replace: false });
  };
  return (
    <Stack spacing={2}>
      <Box>
        <Segmenty opcje={WIDOKI} wybrana={widok} onZmiana={ustawWidok} etykieta="Okres rozliczeń" />
      </Box>
      {widok === 'miesieczne' && <RozliczeniaMiesieczne PanelSwiezosci={PanelSwiezosci} />}
      {widok === 'roczne' && <RozliczeniaRoczne PanelSwiezosci={PanelSwiezosci} />}
      {widok === 'tygodniowe' && <RozliczeniaTygodniowe />}
    </Stack>
  );
}

function RozliczeniaTygodniowe() {
  const [parametry, ustawParametry] = useSearchParams();
  const wybrany = useMemo(() => tydzienZAdresu(parametry), [parametry]);
  const { data, isLoading, isFetching, error } = useRozliczenia(wybrany);
  const [rozwiniete, setRozwiniete] = useState<ReadonlySet<string>>(new Set());

  const ustaw = (t: TydzienIso | undefined) => {
    const p = new URLSearchParams(parametry);
    if (t) { p.set('rok', String(t.rokIso)); p.set('tydzien', String(t.tydzien)); } else { p.delete('rok'); p.delete('tydzien'); }
    ustawParametry(p, { replace: false });
  };

  // Do pokazania wyboru, zanim przyjdzie odpowiedź: wybrany z adresu albo ten z odpowiedzi serwera.
  const okres: TydzienIso | undefined = wybrany ?? (data ? { rokIso: data.period.isoYear, tydzien: data.period.isoWeek } : undefined);
  const opisTygodnia = okres
    ? (() => {
      const o = data && data.period.isoYear === okres.rokIso && data.period.isoWeek === okres.tydzien
        ? { poniedzialek: data.period.monday, piatek: data.period.friday }
        : okresTygodnia(okres.rokIso, okres.tydzien);
      return `Tydzień ${String(okres.tydzien).padStart(2, '0')} · PON ${dzienMiesiac(o.poniedzialek)} – PT ${pelnaData(o.piatek)}`;
    })()
    : 'Tydzień…';

  const [zRuchem, bezRuchu] = useMemo(() => {
    const lista = data?.settlements ?? [];
    return [lista.filter((s) => s.turnoverGrosze !== 0), lista.filter((s) => s.turnoverGrosze === 0)];
  }, [data]);

  const przelacz = (id: string) => setRozwiniete((stare) => {
    const nowe = new Set(stare);
    if (nowe.has(id)) nowe.delete(id); else nowe.add(id);
    return nowe;
  });

  const tydzienTrwa = data ? dzisWarszawa() <= data.period.friday : false;

  return (
    <Stack spacing={2}>
      <NaglowekEkranu
        tytul="Rozliczenia"
        opis={opisTygodnia}
        akcja={okres ? <WyborTygodnia okres={okres} ustaw={ustaw} domyslny={!wybrany} /> : undefined}
      />

      {isLoading && <LoadingState label="Liczenie rozliczeń tygodnia…" />}
      {error ? <ErrorState error={error} context="Restauracje → Rozliczenia." /> : null}

      {data && (
        <>
          <PanelSwiezosci f={data.freshness} />
          {tydzienTrwa && (
            <Alert severity="info">
              <strong>Tydzień jeszcze trwa</strong> — obrót może się zmienić do piątku włącznie
              (nowe zamówienia, usunięcia transakcji).
            </Alert>
          )}
          {data.source.amountState === 'INCOMPLETE' && (
            <Alert severity="warning">
              <strong>{formatCount(data.source.positionsWithoutAmount)} {odmiana(data.source.positionsWithoutAmount, POZYCJA)} bez udowodnionej kwoty pobranej.</strong>{' '}
              Rozliczenia tych restauracji nie mają kwot (oznaczone „kwota niepotwierdzona”) — zamiast liczyć je z ceny katalogowej.
            </Alert>
          )}
          {data.totals.settlementsWithoutAmounts > 0 && data.source.amountState === 'COMPLETE' && (
            <Alert severity="warning">
              <strong>{formatCount(data.totals.settlementsWithoutAmounts)} {odmiana(data.totals.settlementsWithoutAmounts, ROZLICZENIE)} bez kwot</strong> — restauracja
              bez ustalonego miasta (brak w konfiguracji rozliczeń i w systemie głównym — brak stawki). Suma wypłat jest ukryta, bo byłaby niepełna.
            </Alert>
          )}

          {data.visibility && <PanelWidocznosci w={data.visibility} />}

          {data.settlements.length === 0 ? (
            data.visibility && data.visibility.hiddenSettlements > 0 ? (
              <EmptyState
                title="Brak restauracji z obrotem od 1 września 2026"
                description="Lista pokazuje wyłącznie restauracje i grupy z obrotem od 1 września 2026 do dziś — szczegóły w panelu wyżej."
              />
            ) : (
              <EmptyState
                title="Brak restauracji w replice"
                description="Lista restauracji pochodzi z repliki systemu głównego. Pusto znaczy, że replika nie ma jeszcze słownika restauracji."
              />
            )
          ) : (
            <Karta>
              <TableContainer sx={{ maxHeight: '72vh', overflow: 'auto', opacity: isFetching ? 0.6 : 1 }}>
                <Table stickyHeader size="small" aria-label={`Rozliczenia — ${opisTygodnia}`}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, left: 0, zIndex: 3, minWidth: { xs: 150, sm: 220 } }}>Rozliczenie / restauracja</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Miasto</TableCell>
                      {DNI.map((d, i) => (
                        <TableCell key={d} align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {d}
                          <Typography component="div" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            {data.period.days[i] ? dzienMiesiac(data.period.days[i].date) : ''}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Obrót tygodnia</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Marża netto</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        VAT od marży
                        <Typography component="div" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                          {formatStawka(data.rules.commissionVatRate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Prowizja brutto</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'action.selected' }}>
                        Do wypłaty
                        {data.freshness.state !== 'FRESH' && (
                          <Tooltip title={NIEPEWNE}>
                            <Chip size="small" color="warning" variant="outlined" label="niepewne" sx={{ ml: 0.5 }} data-niepewne="kolumna" />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zRuchem.map((s) => (
                      <WierszRozliczenia key={s.settlementGroupId} s={s} rozwiniety={rozwiniete.has(s.settlementGroupId)}
                        przelacz={() => przelacz(s.settlementGroupId)} />
                    ))}
                    {bezRuchu.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={13} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Bez obrotu w tym tygodniu ({formatCount(bezRuchu.length)})
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {bezRuchu.map((s) => (
                      <WierszRozliczenia key={s.settlementGroupId} s={s} rozwiniety={rozwiniete.has(s.settlementGroupId)}
                        przelacz={() => przelacz(s.settlementGroupId)} />
                    ))}
                  </TableBody>
                  <WierszSumy dane={data} />
                </Table>
              </TableContainer>
            </Karta>
          )}

          <Typography variant="caption" component="div" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Obrót = suma kwot pobranych za pozycje zamówień zrealizowanych (Zamówienie, Płatność portfelem), bez pozycji
            usuniętych; dzień = dzień <strong>dostawy</strong> (pon–pt), nigdy dzień zamówienia — tak samo jak w starym panelu.
            Marża netto TuSzamy: {data.rules.warsaw.city} {formatStawka(data.rules.warsaw.marginNet)}, pozostałe miasta{' '}
            {formatStawka(data.rules.otherCities.marginNet)} (stała, bez edycji w panelu); VAT {formatStawka(data.rules.commissionVatRate)} od marży.
            Prowizja i wypłata liczone z dokładnego obrotu i zaokrąglane <strong>raz</strong> do pełnych złotych
            (mnożnik wypłaty: {data.rules.warsaw.city} {data.rules.warsaw.payoutMultiplier.toLocaleString('pl-PL', { maximumFractionDigits: 4 })},
            pozostałe {data.rules.otherCities.payoutMultiplier.toLocaleString('pl-PL', { maximumFractionDigits: 4 })}).
            Usunięcie albo korekta transakcji zmienia rozliczenie samo, gdy dotrze do repliki: zamówienia złożone
            w ostatnich 2 dniach — w ciągu sekund, starsze — dopiero po pełnej synchronizacji (02:00 UTC), dopóki
            synchronizacja nie potwierdzi korekt historii (stan „Korekty i usunięcia starszych zamówień” wyżej).
            Widok przelicza się sam po każdej zmianie danych (sprawdzanie co 10 s).
            {data.source.hasPaidAmountColumn && !data.source.paidAmountColumnActive && (
              <> Kolumna kwoty pobranej (#791) jest w replice, ale <strong>nieaktywna</strong> do czasu bramki aktywacji
              (parytet z systemem głównym) — kwoty liczone dotychczasową, udowodnioną regułą.</>
            )}
          </Typography>
          {data.source.positionsWithoutRestaurant > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatCount(data.source.positionsWithoutRestaurant)} {odmiana(data.source.positionsWithoutRestaurant, POZYCJA)} bez przypisanej restauracji poza rozliczeniami.
            </Typography>
          )}
        </>
      )}

      {!isLoading && !error && !data && (
        <EmptyState title="Brak danych" description="Serwer nie zwrócił rozliczenia." />
      )}
    </Stack>
  );
}

